import React, { useState } from 'react';
import { UserRole, UserTier, User } from '../types';
import { firebaseService } from '../services/firebaseService';
import { API_BASE } from '../apiConfig';
import {
  Mail, Lock, ArrowRight, Loader2, AlertCircle,
  User as UserIcon, LogIn, Chrome, CheckCircle2, ArrowLeft
} from 'lucide-react';

interface Props {
  onLogin: (user: User, isNewUser?: boolean) => void;
  initialMode?: 'login' | 'signup';
}

export const AuthPage: React.FC<Props> = ({ onLogin, initialMode = 'login' }) => {
  const [mode, setMode] = useState<'login' | 'signup' | 'forgot'>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState(false);

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { user, isNewUser } = await firebaseService.signInWithGoogle();
      if (user.email.toLowerCase().trim() === 'admin@priori-ai.com') {
        user.role = UserRole.ADMIN;
        user.tier = UserTier.PREMIUM;
        await firebaseService.updateUser(user.id, { role: UserRole.ADMIN, tier: UserTier.PREMIUM, isActive: true });
      }
      onLogin(user, isNewUser);
    } catch (err: any) {
      setError(err.message || "Google Sign-In Failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { user, isNewUser } = await firebaseService.signInWithApple();
      if (user.email.toLowerCase().trim() === 'admin@priori-ai.com') {
        user.role = UserRole.ADMIN;
        user.tier = UserTier.PREMIUM;
        await firebaseService.updateUser(user.id, { role: UserRole.ADMIN, tier: UserTier.PREMIUM, isActive: true });
      }
      onLogin(user, isNewUser);
    } catch (err: any) {
      setError(err.message || "Apple Sign-In Failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.toLowerCase().trim() })
      });
      if (!res.ok) throw new Error('Failed');
      setResetSuccess(true);
    } catch (err: any) {
      setError("Failed to send reset email. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'forgot') return handleForgotPassword(e);

    setIsLoading(true);
    setError(null);

    const normalizedEmail = email.toLowerCase().trim();

    try {
      if (mode === 'signup') {
        if (password.length < 6) {
          setError("Password must be at least 6 characters.");
          setIsLoading(false);
          return;
        }
        const { user } = await firebaseService.registerWithEmail(normalizedEmail, password, displayName);
        // Admin elevation
        if (normalizedEmail === 'admin@priori-ai.com') {
          user.role = UserRole.ADMIN;
          user.tier = UserTier.PREMIUM;
          await firebaseService.updateUser(user.id, { role: UserRole.ADMIN, tier: UserTier.PREMIUM });
        }
        onLogin(user, true);
      } else {
        const { user } = await firebaseService.signInWithEmail(normalizedEmail, password);
        // Admin elevation
        if (normalizedEmail === 'admin@priori-ai.com') {
          user.role = UserRole.ADMIN;
          user.tier = UserTier.PREMIUM;
          await firebaseService.updateUser(user.id, { role: UserRole.ADMIN, tier: UserTier.PREMIUM });
        }
        onLogin(user, false);
      }
    } catch (err: any) {
      if (err.code === 'auth/user-not-found') setError("Account not found. Please sign up.");
      else if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') setError("Invalid email or password.");
      else if (err.code === 'auth/email-already-in-use') setError("Account already exists. Please log in.");
      else if (err.code === 'auth/weak-password') setError("Password must be at least 6 characters.");
      else if (err.code === 'auth/invalid-email') setError("Please enter a valid email address.");
      else if (err.code === 'auth/too-many-requests') setError("Too many attempts. Please try again later.");
      else setError(err.message || 'Authentication failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 font-sans">
      <div className="w-full max-w-sm space-y-6 fade-in-up">
        
        {/* Header */}
        <div className="text-center space-y-2 mb-8">
          <div className="w-12 h-12 bg-slate-900 rounded-xl mx-auto flex items-center justify-center mb-4">
            <span className="font-bold text-white text-xl">P</span>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
            {mode === 'login' ? 'Sign in' : mode === 'signup' ? 'Create an account' : 'Reset Password'}
          </h2>
          <p className="text-slate-500 text-sm">
            {mode === 'login' ? 'Welcome back to Priori.' : mode === 'signup' ? 'Start organizing your life.' : 'Enter email for recovery.'}
          </p>
        </div>

        {/* Form Container */}
        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
          {error && (
            <div className="p-3 mb-4 bg-red-50 border border-red-100 rounded-lg flex items-center gap-2 text-red-600 text-xs font-medium">
              <AlertCircle size={14} />
              {error}
            </div>
          )}

          {resetSuccess ? (
            <div className="py-6 space-y-6 text-center">
              <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 size={24} />
              </div>
              <div className="space-y-1">
                <h4 className="font-semibold text-slate-900">Check your inbox</h4>
                <p className="text-xs text-slate-500">We sent instructions to {email}</p>
              </div>
              <button 
                type="button"
                onClick={() => { setMode('login'); setResetSuccess(false); }}
                className="w-full py-2.5 bg-slate-100 text-slate-700 font-medium rounded-lg text-sm hover:bg-slate-200 transition-all flex items-center justify-center gap-2"
              >
                <ArrowLeft size={14} /> Back to Login
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              {/* OAuth Buttons */}
              {mode !== 'forgot' && (
                <>
                  <button
                    onClick={handleGoogleLogin}
                    disabled={isLoading}
                    className="w-full py-2.5 bg-white border border-slate-200 rounded-lg flex items-center justify-center gap-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-all"
                  >
                    {isLoading ? <Loader2 className="animate-spin" size={16} /> : <Chrome size={16} />}
                    Continue with Google
                  </button>
                  <button
                    onClick={handleAppleLogin}
                    disabled={isLoading}
                    className="w-full py-2.5 bg-black border border-black rounded-lg flex items-center justify-center gap-2 text-sm font-medium text-white hover:bg-slate-900 transition-all"
                  >
                    {isLoading ? <Loader2 className="animate-spin" size={16} /> : (
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.4c1.3.07 2.2.75 2.96.77 1.14-.24 2.23-.93 3.46-.84 1.46.12 2.56.66 3.28 1.68-2.93 1.82-2.44 5.77.25 6.87-.59 1.55-1.35 3.06-2.95 4.4zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                      </svg>
                    )}
                    Continue with Apple
                  </button>
                  <div className="relative py-2">
                    <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-100"></span></div>
                    <div className="relative flex justify-center text-[10px] uppercase tracking-wider"><span className="bg-white px-2 text-slate-400">Or email</span></div>
                  </div>
                </>
              )}

              <form onSubmit={handleAuth} className="space-y-4">
                {mode === 'signup' && (
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-slate-700">Full Name</label>
                    <input 
                      type="text"
                      required
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    />
                  </div>
                )}
                
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-700">Email</label>
                  <input 
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  />
                </div>
                
                {mode !== 'forgot' && (
                  <div className="space-y-1.5">
                    <div className="flex justify-between">
                      <label className="text-xs font-medium text-slate-700">Password</label>
                      {mode === 'login' && (
                        <button type="button" onClick={() => { setMode('forgot'); setError(null); }} className="text-xs font-medium text-blue-600 hover:text-blue-700">Forgot?</button>
                      )}
                    </div>
                    <input 
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    />
                  </div>
                )}

                <button 
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-2.5 bg-slate-900 text-white font-medium rounded-lg hover:bg-slate-800 transition-all shadow-sm active:scale-[0.98] disabled:opacity-70 mt-2 text-sm"
                >
                  {isLoading ? <Loader2 className="animate-spin mx-auto" size={16} /> : (mode === 'login' ? 'Sign In' : mode === 'signup' ? 'Create Account' : 'Send Reset Link')}
                </button>
              </form>
            </div>
          )}
        </div>

        {/* Footer Switcher */}
        {mode !== 'forgot' && (
          <div className="text-center text-sm text-slate-500">
            {mode === 'login' ? "Don't have an account? " : "Already have an account? "}
            <button 
              onClick={() => { setMode(mode === 'login' ? 'signup' : 'login'); setError(null); }}
              className="font-semibold text-slate-900 hover:underline"
            >
              {mode === 'login' ? 'Sign up' : 'Log in'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};